import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Wordmark } from "./Logo";
import { PHONE, PHONE_LABEL, COMPANY, MAPS_URL, GOOGLE_REVIEW_URL } from "@/lib/api";
import { openCookieSettings } from "./CookieBanner";
import { useT } from "@/lib/i18n";

const TXT = {
  no: { about: "Bilnøkler, chiptuning, ECU/TCU-programmering og bilelektronikk i Stavanger, Sandnes og Rogaland.", phone: "Telefon", email: "E-post", shop: "Verksted", hours: COMPANY.hours, privacy: "Personvern", cookies: "Cookie-innstillinger", review: "Gi oss en anmeldelse på Google", pages: [["/chiptuning", "Chiptuning"], ["/bilnokler", "Bilnøkler"], ["/priser", "Priser"]] },
  en: { about: "Car keys, chiptuning, ECU/TCU programming and car electronics in Stavanger, Sandnes and Rogaland.", phone: "Phone", email: "Email", shop: "Workshop", hours: "Mon–Fri 08:00–16:00", privacy: "Privacy", cookies: "Cookie settings", review: "Review us on Google", pages: [["/chiptuning", "Chiptuning"], ["/bilnokler", "Car keys"], ["/priser", "Prices"]] },
};

export const Footer = () => {
  const t = useT(TXT);
  return (
  <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10 bg-[var(--surface)]">
    <div className="mx-auto max-w-7xl px-5 pt-16 sm:px-8">
      <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <div>
          <Wordmark />
          <p className="mt-4 max-w-xs text-sm text-zinc-400">{t.about}</p>
          <nav className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs uppercase tracking-widest">
            {t.pages.map(([to, label]) => <Link key={to} to={to} className="text-zinc-200 hover:text-[var(--red)]" data-testid={`footer-link-${to.slice(1)}`}>{label}</Link>)}
          </nav>
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-google-review-link"
            className="mt-6 inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-[var(--red)]">
            <Star size={14} className="text-[var(--red)]" /> {t.review}
          </a>
        </div>
        <div className="grid grid-cols-1 gap-8 font-mono text-xs uppercase tracking-widest sm:grid-cols-2 lg:grid-cols-4 lg:gap-10">
          <div className="space-y-3">
            <div className="text-zinc-400">{t.phone}</div>
            <a href={`tel:${PHONE}`} className="block text-zinc-200 hover:text-[var(--red)]" data-testid="footer-phone-link">{PHONE_LABEL}</a>
          </div>
          <div className="space-y-3">
            <div className="text-zinc-400">{t.email}</div>
            <a href={`mailto:${COMPANY.email}`} className="block normal-case tracking-normal text-zinc-200 hover:text-[var(--red)]" data-testid="footer-email-link">{COMPANY.email}</a>
          </div>
          <div className="space-y-3">
            <div className="text-zinc-400">{t.shop}</div>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="block text-zinc-200 hover:text-[var(--red)]" data-testid="footer-address">{COMPANY.address}</a>
            <div className="text-zinc-400" data-testid="footer-hours">{t.hours}</div>
          </div>
          <div className="space-y-3">
            <div className="text-zinc-400">Org.nr.</div>
            <div className="text-zinc-200" data-testid="footer-orgnr">{COMPANY.orgnr}</div>
          </div>
        </div>
      </div>
      <div className="select-none pt-16 font-display text-[22vw] font-black uppercase leading-[0.75] tracking-tighter text-white/[0.04] md:text-[17vw]">
        RB TUN
      </div>
      <div className="flex flex-col gap-3 border-t border-white/10 pb-28 pt-6 font-mono text-[11px] uppercase tracking-widest text-zinc-400 sm:flex-row sm:justify-between">
        <span>© {new Date().getFullYear()} {COMPANY.name} · Org.nr. {COMPANY.orgnr}</span>
        <span className="flex gap-6">
          <Link to="/personvern" data-testid="footer-privacy-link" className="hover:text-zinc-200">{t.privacy}</Link>
          <button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings" className="uppercase tracking-widest hover:text-zinc-200">{t.cookies}</button>
        </span>
      </div>
    </div>
  </footer>
  );
};
